import type { ReactNode } from "react";
import clsx from "clsx";
import { Button } from "./Button";

/**
 * Flat editor section: an icon, a title and a hairline above. The note and
 * POI editors stack these instead of nesting cards, so structure comes from
 * the dividers and headings rather than from borders and fills.
 */
export function EditorSection({
  id,
  title,
  icon,
  actions,
  children,
  className,
}: {
  id: string;
  title: string;
  icon: string;
  /** Controls aligned to the right of the heading. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-labelledby={id}
      className={clsx("min-w-0 border-t border-border pt-4", className)}
    >
      <div className="mb-3 flex min-h-8 flex-wrap items-center gap-x-2.5 gap-y-2">
        <span
          className="material-symbols-outlined text-lg text-secondary"
          aria-hidden
        >
          {icon}
        </span>
        <h3 id={id} className="m-0 mr-auto text-sm font-bold text-primary">
          {title}
        </h3>
        {actions}
      </div>
      {children}
    </section>
  );
}

/**
 * Collapsible editor section. Collapsed, it shows a one-line summary of its
 * content, or an "Add …" prompt when empty. The owner controls `open` so it
 * can decide the initial state once the editor has loaded its values (open
 * when there is data) and start the obvious action when an empty section is
 * opened.
 */
export function EditorDisclosure({
  id,
  title,
  icon,
  open,
  onOpenChange,
  summary,
  emptyLabel,
  touch = false,
  children,
}: {
  id: string;
  title: string;
  icon: string;
  /** Grows the toggle to a 44px touch target (phone task screens). */
  touch?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Collapsed summary; omit or pass null when the section is empty. */
  summary?: ReactNode;
  /** Prompt shown collapsed when there is no summary, e.g. "Add a source". */
  emptyLabel: string;
  children: ReactNode;
}) {
  const panelId = `${id}-panel`;
  const titleId = `${id}-title`;
  const summaryId = `${id}-summary`;
  return (
    <section aria-labelledby={id} className="min-w-0 border-t border-border">
      <h3 className="m-0">
        <Button
          variant="unstyled"
          id={id}
          aria-expanded={open}
          aria-controls={panelId}
          // Named by the title alone; the summary is extra description.
          aria-labelledby={titleId}
          aria-describedby={open ? undefined : summaryId}
          onClick={() => onOpenChange(!open)}
          className={clsx(
            touch && "min-h-11",
            "-mx-2 my-1.5 flex w-[calc(100%+1rem)] min-w-0 cursor-pointer items-center gap-2.5 rounded-lg bg-transparent px-2 py-2 text-left transition-colors hover:bg-subtle focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-info",
          )}
        >
          <span
            className="material-symbols-outlined text-lg text-secondary"
            aria-hidden
          >
            {icon}
          </span>
          <span
            id={titleId}
            className="shrink-0 text-sm font-bold text-primary"
          >
            {title}
          </span>
          <span
            id={summaryId}
            className={clsx(
              "flex min-w-0 flex-1 items-center justify-end gap-1.5 overflow-hidden whitespace-nowrap text-sm font-normal",
              open && "invisible",
            )}
          >
            {summary ?? (
              <span className="font-semibold text-info">{emptyLabel}</span>
            )}
          </span>
          {/* Inline chevron rather than a Material Symbol so it sizes with the
              text instead of the icon font's own 24px. */}
          <svg
            viewBox="0 0 24 24"
            className={clsx(
              "size-4 shrink-0 text-tertiary transition-transform",
              open && "rotate-180",
            )}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </Button>
      </h3>
      <div id={panelId} hidden={!open} className="pb-4 pt-1">
        {open && children}
      </div>
    </section>
  );
}

/** Truncating text plus an optional count, for a disclosure summary. */
export function DisclosureSummary({
  text,
  count,
}: {
  text: string;
  count?: number;
}) {
  return (
    <>
      <span className="min-w-0 truncate text-secondary">{text}</span>
      {count !== undefined && (
        <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-subtle px-1.5 text-2xs font-bold tabular-nums text-secondary">
          {count}
        </span>
      )}
    </>
  );
}
